import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { Box, Tooltip, useTheme } from '@mui/material';
import { useRecoilValue } from 'recoil';
import IntensityHistogramStrip, {
    histogramStripColors,
    STRIP_BAR_TOP_PX,
    STRIP_HEIGHT_PX,
} from '~/viewportGrid/tile/IntensityHistogramStrip';
import { formatOnScale } from '~/domain/dicom/intensityScale';
import { withUnit } from '~/domain/dicom/intensityUnits';
import { strToModality } from '~/domain/dicom/modality';
import {
    BlendSettings,
    FusionColorMapping,
    fusionColorMappingById,
    grayscaleFusionColorMapping,
    pairHasPet,
} from '~/domain/viewport/fusionBlend';
import { imageStreamProgressFamily } from '~/state/atoms/imageStreamingAtoms';
import { WindowLevelSource } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { READOUT, READOUT_FONT } from '../chrome';

/** The colour an image's strip is drawn in: its end of the pair, under the pair's colours. */
export interface HistogramTint {
    readonly accent: 'primary' | 'secondary';
    readonly colorMapping: FusionColorMapping;
}

/**
 * The colour one image's histogram is drawn in. Under a colour-mapped pair
 * the renderer paints each image in its end of the pair's colours, and the
 * strip follows: the row's own image takes the primary end, the fused image
 * the secondary. Outside a pair, or in a pair with a PET (drawn through the
 * PET ramp, so no colour pair applies), the strip takes the plain accent.
 */
export function histogramTintOf(
    accent: HistogramTint['accent'],
    blend: BlendSettings,
    pairModalities: ReadonlyArray<string> | undefined
): HistogramTint {
    const colourMapped =
        pairModalities !== undefined && !pairHasPet(pairModalities);
    return {
        accent,
        colorMapping: colourMapped
            ? fusionColorMappingById(blend.colorMappingId)
            : grayscaleFusionColorMapping,
    };
}

/** Height of the chip that names the image beside its bar, in CSS px. */
const CAPTION_HEIGHT_PX = 12;

/**
 * The widest the name beside a bar is drawn, in CSS px; a longer one is cut
 * short and given in full on hover.
 */
export const HISTOGRAM_CAPTION_MAX_WIDTH_PX = 40;

/** One image whose histogram is drawn, and how it is drawn. */
export interface WindowHistogramImage {
    /** Per-series window/level truth, which also supplies the histogram and the readout's scale. */
    readonly source: WindowLevelSource;
    readonly seriesInstanceUid: SeriesInstanceUid;
    /** Raw modality string, e.g. 'CT'. */
    readonly modality: string;
    readonly tint: HistogramTint;
    /** Suffix for the data-testid, naming which image of the row this is. */
    readonly testIdSuffix: string;
    /**
     * Tells the image from the other one of a pair drawn together, without
     * PHI; absent while the image is drawn alone.
     */
    readonly pairLabel?: string;
}

interface WindowHistogramProps extends WindowHistogramImage {
    /** Width of the strip, its name left out, in CSS px. */
    readonly widthPx: number;
}

/**
 * The intensity histogram of one image, following the window it is
 * displayed with. Its labels go through the readout's own scale, so the
 * strip and the window values never disagree on decimals, and carry the
 * unit: a PET window read in SUV has its edges labelled in SUV, a CT's in
 * HU. An image of a pair is named beside its bar and in its accessible
 * name, so two strips are never told apart by colour alone. Nothing is
 * drawn until the series' volume is whole and its distribution is known.
 */
export default function WindowHistogram({
    source,
    seriesInstanceUid,
    modality,
    tint,
    testIdSuffix,
    pairLabel,
    widthPx,
}: WindowHistogramProps) {
    const theme = useTheme();
    useChangeVersion(source);
    // The histogram is withheld until the last slice lands, and the window
    // source hears nothing when it does: the stream's progress record is
    // what announces it. Reading the record re-renders the strip on that
    // frame, so it appears without waiting for a window gesture.
    useRecoilValue(imageStreamProgressFamily(seriesInstanceUid));
    const windowLevel = source.getWindowLevel();
    const readout = source.getReadout();
    const histogram = source.getIntensityHistogram();
    if (!windowLevel || !readout || !histogram) {
        return null;
    }
    const { scale, valueUnit } = readout;
    const formatValue = (stored: number): string =>
        withUnit(formatOnScale(scale, stored), valueUnit);
    // Two cells of the corner's grid: the name, in a column the names of a
    // pair share so their bars line up, and the strip.
    return (
        <>
            {pairLabel ? (
                <Tooltip title={pairLabel} placement="top">
                    <Box
                        data-testid={`window-histogram-${testIdSuffix}-name`}
                        aria-hidden
                        sx={{
                            // Level with the bar, between its label rows.
                            mt: `${STRIP_BAR_TOP_PX}px`,
                            height: STRIP_HEIGHT_PX,
                            display: 'flex',
                            alignItems: 'center',
                            justifySelf: 'end',
                            maxWidth: HISTOGRAM_CAPTION_MAX_WIDTH_PX,
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: '100%',
                                height: CAPTION_HEIGHT_PX,
                                px: 0.5,
                                borderRadius: 0.5,
                                bgcolor: 'rgba(0, 0, 0, 0.65)',
                                color: READOUT,
                                fontFamily: READOUT_FONT,
                                fontSize: 10,
                                lineHeight: `${CAPTION_HEIGHT_PX}px`,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {pairLabel}
                        </Box>
                    </Box>
                </Tooltip>
            ) : null}
            <Box
                data-testid={`window-histogram-${testIdSuffix}`}
                sx={{
                    display: 'flex',
                    width: widthPx,
                    // Alone, the strip takes the whole row of the grid.
                    gridColumn: pairLabel ? undefined : '1 / -1',
                }}
            >
                <IntensityHistogramStrip
                    histogram={histogram}
                    windowLevel={windowLevel}
                    modality={strToModality(modality)}
                    formatValue={formatValue}
                    colors={histogramStripColors(
                        theme,
                        tint.colorMapping,
                        tint.accent
                    )}
                    testIdSuffix={testIdSuffix}
                    subject={pairLabel}
                />
            </Box>
        </>
    );
}
